import graph from "@irati/utils/services/graph"

import {team} from "./team"
import {survey} from "./surveys"
import {injectValidationStatusHandler} from "./cycleValidationStatusHandler"
import {question} from "./question"
import {interest} from "./interest"
import {driver} from "./driver"


const service = {}

const user = `{
  uuid
  profile_img
  firstname
  lastname
}
`

const emailProperties = `
  bodyHeading
  bodyText
  ctaHref
  ctaText
  subject
`

const emails = `{
  notificationUserIds
  notificationUsers {
    results ${user}
  }

  preSurveyReminder {
    ${emailProperties}
    hoursBefore
  }

  surveyOpenedNotification {
    ${emailProperties}
    enabled
  }

  userDeadlineReminder {
    ${emailProperties}
    hoursBefore
  }

  userReminder {
    ${emailProperties}
    afterDays
  }

  userSurvey {
    ${emailProperties}
  }

  senderName
  senderUsername

}`

const preferences = `{
  actionBoardUrl
  driverCommentTitle
  eNPSPeriodType
  eNPSPeriodValue
  questionSelectionType
  emails ${emails}

  groupIds

  includeActionBoard
  includeDriverComment
  includeENPS

  placeholders {
    tenant
  }

  questionCount
  sendToAll

  teamIds
  teams {
    results {
      ${team}
    }
  }

  userIds
  users {
    results ${user}
  }
}`

const validationError = `
  code
  status
  dev_message
  reference
`


export const schedule = `
  Etag
  createdAt
  id
  interestId
  lastENPSTriggeredAt
  lastTriggeredAt
  nextTriggerAt
  nextTriggerAtOverride
  periodType
  periodValue
  preferences ${preferences}
  reference
  runCount
  startAt
  status
  tenant
  runOnce
  latestSurvey @include (if: $withLatestSurvey) {
     ${survey}
  }
  surveyCount @include (if: $withSurveyCount)
  validationErrors: validationErrorsV2 @include (if: $withValidationErrors){
    ${validationError}
  }
  teamsSentTo {
    results {
      ${team}
    }
  }
  questions: questionsV2 @include (if: $withQuestions){
    total
    count
    results {
      ${question}
    }
  }
  interest @include (if: $withInterest){
    ${interest}
  }
  drivers: driversV2 @include (if: $withDrivers){
    results {
      ${driver}
    }
  }
  responseCount(
    team: $team,
    surveyOpenedAfter: $minOpenedAt,
  ) @include (if: $withResponseCount)
`

// Should default $withValidationErrors to true
export const scheduleParams = `
  $withLatestSurvey: Boolean = false,
  $withResponseCount: Boolean = false,
  $withQuestionStats: Boolean = false,
  $withValidationErrors: Boolean = true,
  $withQuestions: Boolean = false,
  $withQuestionDriver: Boolean = false,
  $withInterest: Boolean = false,
  $withQuestions_Interest: Boolean = false,
  $withDrivers: Boolean = false,
  $withSurveyCount: Boolean = false,
  $team: String = "",
  $driverId: String = ""
  $minOpenedAt: String = ""
`

service.get = (variables, options = {}) => {
  const query = `
  query Schedule(
    $tenant: String!,
    $id: String!,
    ${scheduleParams}
  ) {
    schedule(tenant: $tenant, id: $id) {
      ${schedule}
    }
  }`

  return graph({query, variables}, options)
    .then((response) => injectValidationStatusHandler(response.schedule))
}


service.list = (variables, options = {}) => {
  const query = `
  query ScheduleList(
    $tenant: String!,
    $orderBy: String,
    $orderDirection: String
    ${scheduleParams}
  ) {
    schedules(tenant: $tenant, orderBy: $orderBy, orderDirection: $orderDirection){
      results {
        ${schedule}
      }
    }
  }`

  return graph({query, variables}, options)
    .then((response) => {
      return response.schedules.results.map((eachSchedule) => {
        return injectValidationStatusHandler(eachSchedule)
      })
    })
}

service.create = (variables, options) => {
  const query = `
   mutation CreateScheduleMutation(
    $schedule: ScheduleInput!,
    ${scheduleParams}
   ) {
    createSchedule(schedule: $schedule){
     ${schedule}
    }
  }
  `
  return graph({query, variables}, options).then(({createSchedule}) => createSchedule)
}

service.update = (variables, options) => {
  const query = `
   mutation UpdateScheduleMutation(
    $schedule: ScheduleInput!,
    ${scheduleParams}
   ) {
    schedule(schedule: $schedule){
     ${schedule}
    }
  }
  `
  return graph({query, variables}, options).then(({schedule: response}) => response)
}

service.updateWithSurvey = (variables, options) => {
  const query = `
   mutation UpdateScheduleWithSurveyMutation(
    $schedule: ScheduleInput!,
    $survey: SurveyInput!,
    ${scheduleParams}
   ) {
    schedule(schedule: $schedule){
     ${schedule}
    }
    survey(survey: $survey){
      ${survey}
    }
  }
  `
  return graph({query, variables}, options).then(({schedule: response}) => response)
}

export const getSchedule = service.get
export const getSchedules = service.list
export const createSchedule = service.create
export const updateSchedule = service.update
export const updateScheduleWithSurvey = service.updateWithSurvey

export default service
